import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { Lock, Loader2, CheckCircle } from 'lucide-react';
import { ShareCostLogo } from '@/components/common/ShareCostLogo';
import { useTranslation } from 'react-i18next';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { updatePassword } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(t('web:resetPasswordPage.passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth:form.validation.passwordsDoNotMatch'));
      return;
    }

    setIsLoading(true);
    try {
      await updatePassword(password);
      setSuccess(true);
      setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('web:resetPasswordPage.failedToUpdatePassword'));
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary p-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-text-primary">{t('web:resetPasswordPage.passwordUpdated')}</h2>
          <p className="mt-2 text-text-secondary">
            {t('web:resetPasswordPage.yourPasswordHasBeenChangedSuccessfully')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-secondary p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <ShareCostLogo className="mx-auto mb-4 h-14 w-14 rounded-xl" />
          <h1 className="text-2xl font-bold text-text-primary">{t('web:resetPasswordPage.setNewPassword')}</h1>
          <p className="mt-1 text-text-secondary">{t('web:resetPasswordPage.enterYourNewPasswordBelow')}</p>
        </div>

        <div className="rounded-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary">
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-secondary">
                {t('web:resetPasswordPage.newPassword')}
              </label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-quaternary" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder={t('auth:password.requirement.minLength')}
                  className="w-full rounded-lg border border-border-secondary py-2.5 pl-10 pr-4 text-sm placeholder:text-text-quaternary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-text-secondary">
                {t('auth:form.placeholder.confirmPassword')}
              </label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-quaternary" />
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder={t('web:resetPasswordPage.repeatYourPassword')}
                  className="w-full rounded-lg border border-border-secondary py-2.5 pl-10 pr-4 text-sm placeholder:text-text-quaternary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('web:resetPasswordPage.updatePassword')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
